import { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Loader2, UserX, Edit2, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", position: "", faculty_document: "", notarial_instrument: "", grant_date: "", faculties_scope: "", valid_until: "", notes: "" };

export default function Representatives({ onMutate }) {
  const { user } = useAuth();
  const canRevoke = ["admin", "contador"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {editing?, form}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/legal/representatives");
      setItems(data);
      onMutate?.();
    } catch { toast.error("No se pudieron cargar los representantes"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!modal.form.name.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      const payload = { ...modal.form, grant_date: modal.form.grant_date || null, valid_until: modal.form.valid_until || null };
      if (modal.editing) await api.patch(`/legal/representatives/${modal.editing}`, payload);
      else await api.post("/legal/representatives", payload);
      toast.success("Representante guardado");
      setModal(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const revoke = async (rep) => {
    const reason = prompt(`Motivo de revocación de ${rep.name}:`);
    if (!reason?.trim()) return;
    try {
      await api.post(`/legal/representatives/${rep.id}/revoke`, { reason });
      toast.success("Representante revocado");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo revocar"); }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="legal-representatives-tab">
      <button data-testid="rep-new-btn" onClick={() => setModal({ form: { ...EMPTY } })}
        className="btn-primary px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2">
        <Plus className="w-4 h-4" /> Nuevo representante
      </button>
      {items.length === 0 && <div className="text-sm text-slate-500 italic">Sin representantes registrados.</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((r) => (
          <div key={r.id} className="panel p-4 space-y-1.5" data-testid={`rep-card-${r.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-slate-100">{r.name}</div>
              <span className={`text-[10px] px-2 py-0.5 rounded-sm font-mono uppercase ${r.status === "vigente" ? "text-green-400 bg-green-400/10" : "text-red-400 bg-red-400/10"}`}>
                {r.status}
              </span>
            </div>
            {r.position && <div className="text-sm text-slate-300">{r.position}</div>}
            {r.faculty_document && <div className="text-xs text-slate-400">Acredita: {r.faculty_document}</div>}
            {r.notarial_instrument && <div className="text-xs text-slate-400">Instrumento: <span className="font-mono text-slate-300">{r.notarial_instrument}</span></div>}
            {r.grant_date && <div className="text-xs text-slate-400">Otorgado: {fmtDate(r.grant_date)}</div>}
            {r.faculties_scope && <div className="text-xs text-slate-400">Facultades: {r.faculties_scope}</div>}
            {r.status === "revocado" && r.revoke_reason && (
              <div className="text-xs text-red-300">Revocado: {r.revoke_reason} ({fmtDate(r.revoked_at)})</div>
            )}
            {r.status === "vigente" && (
              <div className="flex gap-2 pt-2">
                <button data-testid={`rep-edit-${r.id}`} onClick={() => setModal({ editing: r.id, form: { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, r[k] || ""])) } })}
                  className="btn-ghost px-2 py-1 text-xs flex items-center gap-1"><Edit2 className="w-3 h-3" /> Editar</button>
                {canRevoke && (
                  <button data-testid={`rep-revoke-${r.id}`} onClick={() => revoke(r)}
                    className="btn-ghost px-2 py-1 text-xs flex items-center gap-1" style={{ borderColor: "#dc2626", color: "#fca5a5" }}>
                    <UserX className="w-3 h-3" /> Revocar</button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="panel w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="rep-modal">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-slate-100">{modal.editing ? "Editar representante" : "Nuevo representante"}</div>
              <button onClick={() => setModal(null)}><X className="w-4 h-4 text-slate-400" /></button>
            </div>
            {[["name", "Nombre completo *"], ["position", "Cargo"], ["faculty_document", "Documento que acredita facultades"],
              ["notarial_instrument", "No. de instrumento notarial"], ["faculties_scope", "Alcance de facultades"]].map(([k, l]) => (
              <div key={k}>
                <label className="label-mono block mb-1">{l}</label>
                <input data-testid={`rep-form-${k}`} value={modal.form[k]} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, [k]: e.target.value } }))}
                  className="w-full input-cyber" />
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-mono block mb-1">Fecha de otorgamiento</label>
                <input type="date" value={modal.form.grant_date || ""} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, grant_date: e.target.value } }))} className="w-full input-cyber" />
              </div>
              <div>
                <label className="label-mono block mb-1">Vigencia (si aplica)</label>
                <input type="date" value={modal.form.valid_until || ""} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, valid_until: e.target.value } }))} className="w-full input-cyber" />
              </div>
            </div>
            <div>
              <label className="label-mono block mb-1">Notas</label>
              <textarea rows={2} value={modal.form.notes} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, notes: e.target.value } }))} className="w-full input-cyber" />
            </div>
            <button data-testid="rep-save-btn" onClick={save} disabled={saving}
              className="btn-primary w-full py-2 text-xs uppercase tracking-widest flex items-center justify-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
